import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Link,
  Grid,
  FormControl,
  Tooltip,
  Autocomplete,
  CircularProgress,
  Divider,
  DialogContent,
  DialogActions,
  Dialog,
  IconButton,
  Backdrop,
} from "@mui/material";
import {
  AddressDropdownItem,
  OptionGroup,
  SavedBillingPayload,
} from "@/app/models/checkout/types";
import dataService from "@/app/services/dataService";
import { useSelector } from "react-redux";
import { Lookup } from "@/app/models/common/types";
import { RootState } from "../../store/store";
import { AttachPdfPopup } from "./attachPdfPopup";
import {
  clearFromStorage,
  loadFilesFromStorage,
  loadFromStorage,
} from "@/app/utils/localStorageHelper/checkoutStorage";
import { STORAGE_KEYS } from "@/app/utils/localStorageHelper/checkoutKeys";
import { useGlobalNotification } from "@/app/components/header/GlobalNotificationContext";
import CloseIcon from "@mui/icons-material/Close";
import ShipToConfirmationDialog from "@/app/components/header/shipToConfirmationDialog";

export interface BillingInformationProps {
  customerNum: string | null;
  customerName: string | null;
  customerShipToName: string | null;
  customerShipToId: number;
  customerShipToAddress: string | null;
  onSave: (payload: SavedBillingPayload) => void;
}

const BillingInformation: React.FC<BillingInformationProps> = ({
  customerNum,
  customerName,
  customerShipToName,
  customerShipToId,
  customerShipToAddress,
  onSave,
}) => {
  const customerNumber = useSelector(
    (state: RootState) => state.userState.user?.customerNumber ?? null
  );
  const [jobName, setJobName] = useState("");
  const [customerShipTos, setCustomerShipTos] = useState<OptionGroup[]>([]);
  const [selectedValue, setSelectedValue] = useState<number>();
  const [loading, setLoading] = useState<boolean>(false);
  const [attachPopupOpen, setAttachPopupOpen] = React.useState<boolean>(false);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [newShipToId, setNewShipToId] = useState<number | null>(null);
  const [attachments, setAttachments] = React.useState<File[]>([]);
  const { setNotification } = useGlobalNotification();
  const userId =
    useSelector((state: RootState) => state.userState.user.id)?.toString() ??
    "0";
  const [branchUpdateOpen, setBranchUpdateOpen] = useState(false);
  const [warehouseName, setWarehouseName] = useState("");
  const [pageLoading, setPageLoading] = useState(false);

  const [pendingSapConfirmation, setPendingSapConfirmation] = useState<{
    currentShipToName?: string;
    branchName?: string;
  } | null>(null);
  const [shipToConfirmationOpen, setShipToConfirmationOpen] = useState(false);
  const [shipToConfirmationDetails, setShipToConfirmationDetails] = useState<{
    currentShipToName?: string;
    branchName?: string;
  } | null>(null);
  const [isConfirmingShipTo, setIsConfirmingShipTo] = useState(false);

  useEffect(() => {
    const fetchAllCustomerShipTos = async () => {
      setLoading(true);
      try {
        const response = await dataService.getAllCustomerShipTos(
          customerNumber
        );
        const shipTos: Lookup[] = response.data ?? [];
        if (shipTos.length > 0) {
          const grouped: OptionGroup[] = [
            {
              label: "Addresses",
              options: shipTos.map((addr: Lookup) => ({
                id: addr.id,
                name: addr.displayName,
              })),
            },
          ];

          setCustomerShipTos(grouped);

          if (customerShipToId > 0) {
            setSelectedValue(customerShipToId);
          } else {
            const first = grouped?.[0]?.options?.[0]?.id;
            if (first) setSelectedValue(Number(first));
          }
        }
      } catch (error) {
        console.error("Failed to load templates:", error);
      } finally {
        setLoading(false);
      }
    };

    const cached = loadFromStorage<SavedBillingPayload>(
      userId,
      STORAGE_KEYS.BILLING
    );
    if (cached) {
      setJobName(cached.jobName);
    }

    const attachments = loadFilesFromStorage(
      userId,
      STORAGE_KEYS.BILLING_ATTACHMENTS
    );
    if (attachments) {
      setAttachments(attachments);
    }
    clearFromStorage(userId, STORAGE_KEYS.BILLING);
    clearFromStorage(userId, STORAGE_KEYS.BILLING_ATTACHMENTS);
    fetchAllCustomerShipTos();
  }, []);

  const handleUpload = (files: File[]) => {
    setAttachments(files);
  };

  const handleShipToUpdate = async (newShipToId: number) => {
    if (!newShipToId || newShipToId === customerShipToId) return;

    setPageLoading(true);
    const response = await dataService.validateCustomerShipToUpdate(
      newShipToId
    );
    setPageLoading(false);

    const validation = response.data;
    setNewShipToId(newShipToId);
    setWarehouseName(validation.warehouseName ?? "");

    if (validation.needsUserConfirmation) {
      setConfirmationMessage(validation.message ?? "");
      setPendingSapConfirmation(
        validation.needsSapConfirmation
          ? {
              currentShipToName: validation.currentShipToName,
              branchName: validation.warehouseName,
            }
          : null
      );
      setConfirmationOpen(true);
      return;
    }

    if (validation.needsSapConfirmation) {
      setShipToConfirmationDetails({
        currentShipToName: validation.currentShipToName,
        branchName: validation.warehouseName,
      });
      setShipToConfirmationOpen(true);
      return;
    }

    await applyShipToUpdate(newShipToId, false);
  };

  const applyShipToUpdate = async (shipToId: number, branchChanged = true) => {
    setIsConfirmingShipTo(true);
    try {
      const response = await dataService.setSelectedShipTo(shipToId.toString());
      if (response.success === true) {
        if (branchChanged) setBranchUpdateOpen(true);
      } else {
        console.error("Error setting ship to");
      }
    } finally {
      setIsConfirmingShipTo(false);
    }
  };

  const handleConfirmationClose = async (
    confirmed: boolean,
    shipToId: number
  ) => {
    setConfirmationOpen(false);

    if (!confirmed) {
      setPendingSapConfirmation(null);
      return;
    }

    if (pendingSapConfirmation) {
      setShipToConfirmationDetails(pendingSapConfirmation);
      setPendingSapConfirmation(null);
      setShipToConfirmationOpen(true);
      return;
    }

    await applyShipToUpdate(shipToId);
  };

  const handleShipToConfirmationClose = async (confirmed: boolean) => {
    setShipToConfirmationOpen(false);
    setShipToConfirmationDetails(null);

    if (confirmed && newShipToId !== null) {
      await applyShipToUpdate(newShipToId);
    }
  };

  const handleContinue = () => {
    if (!jobName.trim()) {
      setNotification("Job Name is required", "error");
      return;
    }

    const name = customerShipToName?.trim() || "";
    const address = customerShipToAddress?.trim() || "";
    const currentBillingAccount =
      !name && !address ? null : `${name} ${address}`.trim();

    onSave({
      jobName: jobName.trim(),
      currentBillingAccount: currentBillingAccount,
      attachments,
    });
  };

  const handleRemoveFile = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          zIndex: 1600,
        }}
        open={pageLoading}
      >
        <CircularProgress color="primary" />
      </Backdrop>
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box className="circleNumCheckout" sx={{ mr: 2 }}>
            <Typography className="circleNumCheckoutTypo">1</Typography>
          </Box>
          <Typography className="checkout-container-title">
            Billing Information
          </Typography>
          <Divider className="checkout-divider" />
        </Box>
        <Box
          sx={{
            border: "10px solid",
            borderColor: "grey.300",
            borderRadius: 2,
            p: 3,
          }}
        >
          <Typography className="billing-sub-title">Billing Account</Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography fontWeight="bold" sx={{ mr: 1 }}>
                    Account Number:
                  </Typography>
                  <Typography>{customerNum}</Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography fontWeight="bold" sx={{ mr: 1 }}>
                    Customer Name:{" "}
                    <span style={{ fontWeight: "normal" }}>{customerName}</span>
                  </Typography>
                </Box>
              </Box>
            </Grid>
            {customerShipTos &&
              customerShipTos.length > 0 &&
              customerShipTos[0].options.length > 1 && (
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography fontWeight="bold">
                      Current Billing Account:
                    </Typography>
                  </Box>
                  <Box sx={{ mb: 2 }}>
                    <Typography>{customerShipToName}</Typography>
                    <Typography>{customerShipToAddress}</Typography>
                  </Box>
                  <Box sx={{ mb: 6 }}>
                    <FormControl fullWidth size="small">
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexDirection: { xs: "column", sm: "row" },
                        }}
                      >
                        <Autocomplete<AddressDropdownItem>
                          fullWidth
                          size="small"
                          options={customerShipTos.flatMap(
                            (group) => group.options
                          )}
                          groupBy={(option) => {
                            const group = customerShipTos.find((g) =>
                              g.options.some((o) => o.id === option.id)
                            );
                            return group?.label ?? "";
                          }}
                          getOptionLabel={(option) => option.name}
                          value={
                            customerShipTos
                              .flatMap((g) => g.options)
                              .find(
                                (opt) => opt.id === selectedValue?.toString()
                              ) || null
                          }
                          isOptionEqualToValue={(option, value) =>
                            option.id === value?.id
                          }
                          onChange={(_, newValue) => {
                            setSelectedValue(Number(newValue?.id ?? ""));
                          }}
                          componentsProps={{
                            popper: {
                              sx: {
                                zIndex: 10000,
                              },
                            },
                          }}
                          renderGroup={(params) => (
                            <li key={params.key}>
                              <div
                                style={{
                                  fontWeight: "bold",
                                  padding: "0px 15px",
                                }}
                              >
                                {params.group}
                              </div>
                              <ul style={{ paddingLeft: 16 }}>
                                {params.children}
                              </ul>
                            </li>
                          )}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              variant="outlined"
                              fullWidth
                              className="form-input"
                              placeholder="Search list..."
                              InputLabelProps={{ shrink: false }}
                              size="small"
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {loading && <CircularProgress size={20} />}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                        />
                        <Button
                          variant="text"
                          onClick={() =>
                            handleShipToUpdate(Number(selectedValue))
                          }
                          sx={{
                            ml: 1,
                            minWidth: "auto",
                            p: 0,
                            textTransform: "none",
                          }}
                        >
                          Update
                        </Button>
                      </Box>
                    </FormControl>
                  </Box>
                </Grid>
              )}
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12} md={5}>
              <Typography fontWeight="medium" gutterBottom>
                Job Name
                <Typography component="span" color="error" fontWeight="medium">
                  *
                </Typography>
              </Typography>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={jobName}
                inputProps={{ maxLength: 22 }}
                onChange={(e) => setJobName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mt: { xs: 0, sm: 5 },
                }}
              >
                <Box
                  sx={{ display: "flex", alignItems: "center" }}
                  onClick={() => setAttachPopupOpen(true)}
                >
                  <Link href="#" underline="hover">
                    Attach PDF
                  </Link>
                  <Tooltip title="Upload a PDF pertaining to this Job" arrow>
                    <Box
                      sx={{
                        width: 20,
                        height: 20,
                        borderRadius: "50%",
                        bgcolor: "primary.main",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        ml: 1,
                      }}
                    >
                      <Typography
                        variant="caption"
                        color="common.white"
                        fontWeight="bold"
                      >
                        i
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </Box>
              <AttachPdfPopup
                savedFiles={attachments}
                open={attachPopupOpen}
                onClose={() => setAttachPopupOpen(false)}
                onUpload={handleUpload}
                maxFiles={6}
              />
            </Grid>
          </Grid>
          <Grid container direction="column">
            {attachments.map((file: File, i: number) => {
              const fileUrl = URL.createObjectURL(file);
              return (
                <Box
                  key={i}
                  sx={{
                    bgcolor: "transparent",
                    "&:hover": {
                      ".remove-btn": { opacity: 1, visibility: "visible" },
                    },
                  }}
                >
                  <Link
                    className="attachment-file-name"
                    key={i}
                    href={fileUrl}
                    underline="hover"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {file.name}
                  </Link>

                  <IconButton
                    className="remove-btn"
                    size="small"
                    onClick={() => handleRemoveFile(i)}
                    sx={{
                      marginLeft: "8px",
                      color: "#737373",
                      opacity: 0,
                      visibility: "hidden",
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                </Box>
              );
            })}
          </Grid>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={12}>
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="outlined"
                  color="primary"
                  className="blueButtonOutline"
                  onClick={handleContinue}
                >
                  Continue
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
        <Dialog
          open={confirmationOpen}
          onClose={() => setConfirmationOpen(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogContent>
            <Typography>{confirmationMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined"
              color="primary"
              className="blueButtonOutline"
              onClick={() =>
                newShipToId !== null &&
                handleConfirmationClose(true, newShipToId)
              }
              autoFocus
            >
              CONTINUE
            </Button>
            <Button
              variant="outlined"
              color="primary"
              className="blueButtonOutline"
              onClick={() =>
                newShipToId !== null &&
                handleConfirmationClose(false, newShipToId)
              }
            >
              CANCEL
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={branchUpdateOpen}
          onClose={() => setBranchUpdateOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              width: "100%",
              maxWidth: "500px",
              margin: "auto",
            },
          }}
        >
          <DialogContent>
            <Typography sx={{ textAlign: "center" }}>
              Your branch has been successfully updated to:&nbsp;
              <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                {warehouseName}
              </Box>
              .
            </Typography>
            <Typography sx={{ textAlign: "center", mt: 1 }}>
              If you need to change your branch, you can in the top corner.
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: "center" }}>
            <Button
              variant="outlined"
              color="primary"
              className="blueButtonOutline"
              onClick={() => {
                setBranchUpdateOpen(false);
                window.location.reload();
              }}
              autoFocus
            >
              CLOSE
            </Button>
          </DialogActions>
        </Dialog>

        <ShipToConfirmationDialog
          open={shipToConfirmationOpen}
          details={shipToConfirmationDetails}
          isSubmitting={isConfirmingShipTo}
          onConfirm={() => handleShipToConfirmationClose(true)}
          onCancel={() => handleShipToConfirmationClose(false)}
        />

        {isConfirmingShipTo && (
          <Box
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              bgcolor: "transparent",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2500,
              pointerEvents: "auto",
            }}
          >
            <CircularProgress size={40} sx={{ color: "#1976d2" }} />
          </Box>
        )}
      </Box>
    </>
  );
};

export default BillingInformation;
